// This file is a copy of the List.tsx comonenet from the @modernatx/ui-kit-react package < v5.
// List was removed from that package, so we need to support it here for the BlockTextV2 component.
import { Box, BoxDiv, BoxSx, Text } from "@modernatx/ui-kit-react-v2";
import React from "react";

interface ListOnlyProps {
  size?: React.ComponentProps<typeof Text>["size"];
  start?: number;
  variant?: "ordered" | "unordered" | "unordered-accent";
}

type ListContextValue = ListOnlyProps & {
  getOrderedCount: () => number;
};

type ListProps = Omit<BoxDiv, "size"> & ListOnlyProps;

const ListContext = React.createContext<ListContextValue>({
  getOrderedCount: () => 0
});

export const List = React.forwardRef<HTMLUListElement, ListProps>(
  ({ children, size, start = 1, sx, variant: variantInitial, ...props }, ref) => {
    const { variant: variantContext } = React.useContext(ListContext);
    const count = React.useRef(start - 1);
    const getOrderedCount = () => {
      count.current = count.current + 1;
      return count.current;
    };
    const variant = variantInitial || variantContext || "unordered";

    // Reset the count on every render
    count.current = start - 1;

    const content = (
      <Box
        as={variant === "unordered" ? "ul" : "ol"}
        {...props}
        ref={ref}
        sx={{
          fontSize: "inherit",
          lineHeight: "inherit",
          ...sx,
          display: "grid",
          gridColumnGap: variant === "unordered-accent" ? 3 : 2,
          gridTemplateColumns: "auto 1fr",
          listStyle: "none"
        }}
      >
        <ListContext.Provider value={{ getOrderedCount, start, variant }}>
          {children}
        </ListContext.Provider>
      </Box>
    );

    return size ? <Text size={size}>{content}</Text> : content;
  }
);

List.displayName = "List";

export const ListItem = React.forwardRef<HTMLLIElement, React.ComponentProps<typeof Box>>(
  ({ children, sx, ...props }, ref) => {
    const { getOrderedCount, variant } = React.useContext(ListContext);
    const marker = React.useMemo(() => {
      switch (variant) {
        case "ordered":
          return `${getOrderedCount()}.`;
        case "unordered":
          return "•";
        case "unordered-accent":
          return "◉";
      }
    }, [getOrderedCount, variant]);
    const whitespaceY: BoxSx = {
      marginBlockEnd: sx?.marginBlockEnd,
      marginBlockStart: sx?.marginBlockStart,
      marginBottom: sx?.marginBottom,
      marginTop: sx?.marginTop,
      mb: sx?.mb,
      mt: sx?.mt,
      my: sx?.my,
      paddingBlockEnd: sx?.paddingBlockEnd,
      paddingBlockStart: sx?.paddingBlockStart,
      paddingBottom: sx?.paddingBottom,
      paddingTop: sx?.paddingTop,
      pb: sx?.pb,
      pt: sx?.pt,
      py: sx?.py
    };

    return (
      <Box
        as="li"
        {...props}
        ref={ref}
        sx={{
          fontSize: "inherit",
          lineHeight: "inherit",
          ...sx,
          display: "contents"
        }}
      >
        <Box
          as="span"
          aria-hidden={true}
          sx={{
            ...whitespaceY,
            // todo - match color to theme - when porting over from v1 tokens, I modified the color to be color-current-brand-950 - not sure if this is correct. This matched the v1 color token of accent01 in the CovidDisease theme
            color: variant === "unordered-accent" ? "color-current-brand-950" : "currentColor",
            fontSize: "inherit",
            lineHeight: "inherit",
            textAlign: "end"
          }}
        >
          {marker}
        </Box>
        <Box
          as="span"
          sx={{
            ...whitespaceY,
            fontSize: "inherit",
            lineHeight: "inherit"
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }
);

ListItem.displayName = "ListItem";
